import { Injectable } from '@angular/core';
import { Auth, onAuthStateChanged, User } from '@angular/fire/auth';
import {
  doc,
  Firestore,
  getDoc,
  serverTimestamp,
  setDoc,
} from '@angular/fire/firestore';
import { buildAllPagePermissions } from '../utils/page-permissions';

export interface GuardUserSnapshot {
  firebaseUser: User | null;
  userData: any | null;
}

@Injectable({ providedIn: 'root' })
export class GuardUserCacheService {
  private cachedUid = '';
  private cachedUserData: any | null = null;
  private cachedAt = 0;
  private inFlight?: Promise<GuardUserSnapshot>;
  private readonly ttlMs = 5 * 60 * 1000;

  constructor(
    private readonly auth: Auth,
    private readonly firestore: Firestore,
  ) {}

  async getSnapshot(): Promise<GuardUserSnapshot> {
    const firebaseUser = await this.getFirebaseUser();

    if (!firebaseUser?.uid) {
      this.clear();
      return { firebaseUser: null, userData: null };
    }

    const now = Date.now();
    if (
      this.cachedUid === firebaseUser.uid &&
      this.cachedUserData &&
      now - this.cachedAt < this.ttlMs
    ) {
      return {
        firebaseUser,
        userData: this.cachedUserData,
      };
    }

    if (this.inFlight) {
      return this.inFlight;
    }

    const userRef = doc(this.firestore, 'users', firebaseUser.uid);

    this.inFlight = getDoc(userRef)
      .then(async (snapshot) => {
        if (!snapshot.exists()) {
          this.clear();
          return { firebaseUser, userData: null };
        }

        let userData: any = snapshot.data() || {};
        const normalizedRole = String(userData?.role || '')
          .toLowerCase()
          .replace(/[\s_-]+/g, '');

        /*
         * PermissionGuard and the sidebar require an individual permission
         * object for every menu key. Repair old/new Super Admin records that
         * contain empty permissions or the unsupported { all: true } shortcut.
         */
        if (normalizedRole === 'superadmin') {
          const allPagePermissions = buildAllPagePermissions();
          const existingPermissions = userData?.permissions || {};

          const requiresPermissionRepair = Object.keys(
            allPagePermissions,
          ).some((key) => {
            const requiredPermission: any = allPagePermissions[key];
            const permission = existingPermissions?.[key] || {};

            return Object.keys(requiredPermission).some(
              (action) => requiredPermission[action] === true && permission?.[action] !== true,
            );
          });

          if (requiresPermissionRepair) {
            await setDoc(
              userRef,
              {
                permissions: allPagePermissions,
                updatedAt: serverTimestamp(),
              },
              { merge: true },
            );

            userData = {
              ...userData,
              permissions: allPagePermissions,
            };
          }
        }

        this.cachedUid = firebaseUser.uid;
        this.cachedUserData = userData;
        this.cachedAt = Date.now();
        localStorage.setItem('userData', JSON.stringify({ uid: firebaseUser.uid, ...userData }));

        return { firebaseUser, userData };
      })
      .finally(() => {
        this.inFlight = undefined;
      });

    return this.inFlight;
  }

  clear(): void {
    this.cachedUid = '';
    this.cachedUserData = null;
    this.cachedAt = 0;
    this.inFlight = undefined;
  }

  private getFirebaseUser(): Promise<User | null> {
    if (this.auth.currentUser) {
      return Promise.resolve(this.auth.currentUser);
    }

    return new Promise<User | null>((resolve) => {
      const unsubscribe = onAuthStateChanged(this.auth, (user) => {
        unsubscribe();
        resolve(user);
      });
    });
  }
}
